import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  MdArrowDownward,
  MdArrowForward,
  MdOutlineArrowUpward,
  MdOutlineCreditScore,
  MdOutlineTune,
} from "react-icons/md";
import { motion } from "framer-motion";
import {
  COUNT_UP_ANIMATION_DURATION,
  ETransactionType,
  ICON_SIZES,
} from "src/helpers/constants";
import { memo, useCallback, useId, useMemo, useState } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import Icon from "../Icon/Icon";
import { Link, useNavigate } from "react-router-dom";
import useCurrentUser from "src/hooks/useCurrentUser";
import {
  formatCurrency,
  formatLongDate,
  getCurrencyDataByCode,
  rgbaObjectToString,
  transactionDescription,
} from "src/helpers/utils";
import { ERecurringTransactionType } from "../RecurringTransaction/RecurringTransactionsCollapsible";
import ClientProviderInfoModal from "../ClientProviderInfoModal/ClientProviderInfoModal";
import useMobile from "src/hooks/useMobile";
import { Tooltip } from "react-tooltip";
import RecurringTransactionSettings from "../RecurringTransaction/RecurringTransactionSettings";
import RecurringTransactionPayment from "../RecurringTransaction/RecurringTransactionPayment";
import { EButtonKind } from "../Button/ButtonTypes";
import CountUp from "react-countup";

const MOBILE_SIZE = 58;
const DESKTOP_SIZE = 64;

interface IRecurringTransactionCard {
  transaction: any;
  type: ERecurringTransactionType;
  index: number;
  showSettingsButton?: boolean;
}

const RecurringTransactionCard: React.FC<IRecurringTransactionCard> = ({
  transaction,
  type,
  index,
  showSettingsButton = true,
}) => {
  const { isMobile } = useMobile();
  const { getSourceCustomSettings } = useAllTransactions();
  const sourceTooltipId = useId();
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showRecurringSettings, setShowRecurringSettings] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { getAccountById } = useCurrentUser();

  const sourceInfo = useMemo(() => {
    return getSourceCustomSettings(transaction?.payTo);
  }, [transaction]);

  const accountInfo = useMemo(() => {
    return getAccountById(transaction?.account);
  }, [transaction]);

  const currencySymbol = useMemo(() => {
    return getCurrencyDataByCode({ code: accountInfo?.currency })?.symbol || "";
  }, [accountInfo]);

  const description = useMemo(() => {
    return transactionDescription(transaction);
  }, [transaction]);

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  return (
    <>
      <motion.div
        className="flex w-full"
        initial={{
          opacity: 0,
          x: index % 2 === 0 ? -100 : 100,
        }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
          delay: index * 0.1,
        }}
      >
        <Link
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "bg-sky-200/60 dark:bg-gray-900/50",
            "p-3 md:p-3",
            "rounded-md",
            "md:gap-4",
            "gap-2",
          ])}
          to={`/transaction/${accountInfo?.account}/${transaction?.invoice}`}
        >
          <div className={clsx(["flex", "flex-row", "gap-2"])}>
            <div
              className={clsx([
                `!min-w-[${MOBILE_SIZE}px]`,
                `!min-h-[${MOBILE_SIZE}px]`,
                `!max-w-[${MOBILE_SIZE}px]`,
                `!max-h-[${MOBILE_SIZE}px]`,
                `md:!min-w-[${DESKTOP_SIZE}px]`,
                `md:!min-h-[${DESKTOP_SIZE}px]`,
                `md:!max-w-[${DESKTOP_SIZE}px]`,
                `md:!max-h-[${DESKTOP_SIZE}px]`,
                "rounded-md",
                "overflow-hidden",
                "inline-flex",
                "items-center",
                "justify-center",
                "cursor-pointer",
                "relative",
              ])}
              data-tooltip-id={sourceTooltipId}
              data-tooltip-content={isMobile ? "" : "Información de contacto"}
              data-tooltip-position-strategy="fixed"
              data-tooltip-open-on="hover"
              data-tooltip-offset={0}
              onClick={(e: any) => {
                e?.preventDefault?.();
                setShowSourceModal(true);
              }}
              style={
                sourceInfo?.picture
                  ? {}
                  : {
                      background: rgbaObjectToString(sourceInfo?.bg),
                      color: rgbaObjectToString(sourceInfo?.color),
                    }
              }
            >
              {sourceInfo?.picture ? (
                <img
                  src={sourceInfo?.picture?.thumbnailUrl}
                  className={clsx(["bg-checkers", "!w-full", "!h-full"])}
                  alt={transaction?.payTo}
                />
              ) : (
                <Icon
                  name={sourceInfo?.icon}
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                />
              )}
              {!isMobile && (
                <Tooltip
                  id={sourceTooltipId}
                  style={{ zIndex: 99 }}
                  positionStrategy="fixed"
                />
              )}
            </div>
            <div className={clsx(["flex", "flex-col", "w-full"])}>
              <div
                className={clsx([
                  "text-sm",
                  "md:text-base",
                  "font-normal",
                  "truncate",
                ])}
              >
                {description}
              </div>
              <div className={clsx(["text-sm", "truncate"])}>
                {`${
                  transaction?.type === ETransactionType.INCOME
                    ? "Ingreso"
                    : "Gasto"
                } de ${transaction?.payTo}`}
              </div>
              <div className={clsx(["text-sm", "font-medium", "truncate"])}>
                {formatLongDate(transaction?.recurring?.nextPayment)}
              </div>
            </div>
            <div
              className={clsx([
                "flex",
                "items-center",
                "justify-center",
                "text-4xl",
                "flex-col",
              ])}
            >
              {type === ERecurringTransactionType.TODAY ? (
                <></>
              ) : (
                <>
                  <span
                    className={clsx([
                      "font-normal",
                      {
                        hidden:
                          transaction.daysUntilDue === 0 &&
                          type === ERecurringTransactionType.UPCOMING,
                        "dark:text-purple-400 text-red-500":
                          type === ERecurringTransactionType.OVERDUE,
                      },
                    ])}
                  >
                    {type === ERecurringTransactionType.OVERDUE
                      ? transaction.daysOverdue
                      : transaction.daysUntilDue}
                  </span>
                  <span className={clsx(["text-sm"])}>
                    {type === ERecurringTransactionType.OVERDUE ? (
                      <>
                        {transaction.daysOverdue === 1
                          ? "día"
                          : transaction.daysOverdue === 0
                          ? "Hoy"
                          : "días"}
                      </>
                    ) : (
                      <>
                        {transaction.daysUntilDue === 1
                          ? "día"
                          : transaction.daysUntilDue === 0
                          ? "Hoy"
                          : "días"}
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-row",
              "gap-6",
              "justify-between",
              "items-center",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "items-center",
                "text-xl",
                "md:text-2xl",
                "font-medium",
                "truncate",
                "dark:text-purple-400",
                "text-blue-400",
              ])}
            >
              {transaction?.type === ETransactionType.INCOME && (
                <MdArrowDownward
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                  className={clsx(["text-red-500", "dark:text-cyan-400"])}
                />
              )}
              {transaction?.type === ETransactionType.EXPENSE && (
                <MdOutlineArrowUpward
                  size={isMobile ? ICON_SIZES.MD : ICON_SIZES.MD_LG}
                  className={clsx(["text-red-500", "dark:text-cyan-400"])}
                />
              )}

              <CountUp
                end={transaction?.total}
                duration={COUNT_UP_ANIMATION_DURATION}
                separator=","
                decimals={hasDecimals(transaction?.total) ? 2 : 0}
                delay={0.1}
                preserveValue
                prefix={currencySymbol}
              />
            </div>
            <ButtonGroup
              buttons={[
                {
                  tooltip: "Recurrencia",
                  icon: <MdOutlineTune size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();

                    setShowRecurringSettings(true);
                  },
                  hide: !showSettingsButton,
                },
                {
                  tooltip: `Registrar ${
                    transaction?.type === ETransactionType.INCOME
                      ? "ingreso"
                      : "gasto"
                  }`,
                  icon: <MdOutlineCreditScore size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();

                    setShowPaymentModal(true);
                  },
                  kind: EButtonKind.SAVE,
                },
                {
                  tooltip: "Ver transacción",
                  icon: <MdArrowForward size={ICON_SIZES.MD} />,
                  hide: isMobile,
                  onClick: (e: any) => {
                    e?.preventDefault?.();

                    navigate(
                      `/transaction/${accountInfo?.account}/${transaction?.invoice}`
                    );
                  },
                },
              ]}
              align="right"
            />
          </div>
        </Link>
      </motion.div>

      {showSourceModal && (
        <ClientProviderInfoModal
          clientProvider={transaction?.payTo}
          onClose={() => setShowSourceModal(false)}
        />
      )}

      {showRecurringSettings && (
        <RecurringTransactionSettings
          onClose={() => {
            setShowRecurringSettings(false);
          }}
          transaction={transaction?.invoice}
          currentSettings={transaction?.recurring}
          onSave={() => {
            setShowRecurringSettings(false);
          }}
        />
      )}

      {showPaymentModal && (
        <RecurringTransactionPayment
          transaction={transaction}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
    </>
  );
};

export default memo(RecurringTransactionCard);
